/**
 * O que faz: Exibe insights automáticos sobre atividade, anúncios e conversão do período.
 * Onde usa: Componente Dashboard principal.
 * Conexões: Recebe dados processados do Supabase via props e gera insights dinâmicos.
 * Tabelas: Não acessa banco; consome métricas agregadas.
 * Data: 11/06/2026
 */

import { Sparkles, Megaphone, TrendingUp, Inbox, LucideIcon } from 'lucide-react';
import { DashboardMetrics } from '@/hooks/dashboard/types';

interface DashboardInsightsProps {
  dashboardData: DashboardMetrics | null;
}

interface Insight {
  icon: LucideIcon;
  iconClass: string;
  chipClass: string;
  title: string;
  text: string;
}

export const DashboardInsights = ({ dashboardData }: DashboardInsightsProps) => {
  const insights: Insight[] = [];

  if (dashboardData && dashboardData.totalContatos > 0) {
    insights.push({
      icon: Sparkles,
      iconClass: 'text-success-text',
      chipClass: 'bg-success-subtle',
      title: 'Atividade no período',
      text: `${dashboardData.totalContatos} novos contatos no período selecionado.`
    });

    if (dashboardData.leadsComAdName > 0) {
      insights.push({
        icon: Megaphone,
        iconClass: 'text-violet-600',
        chipClass: 'bg-violet-50',
        title: 'Performance de anúncios',
        text: `${dashboardData.leadsComAdName} leads vieram de anúncios (${((dashboardData.leadsComAdName / dashboardData.totalContatos) * 100).toFixed(1)}% do total).`
      });
    }

    if (dashboardData.taxaConversao > 0) {
      insights.push({
        icon: TrendingUp,
        iconClass: 'text-blue-600',
        chipClass: 'bg-blue-50',
        title: 'Taxa de conversão',
        text: `Sua taxa de conversão no período foi de ${dashboardData.taxaConversao}%.`
      });
    }
  } else {
    insights.push({
      icon: Inbox,
      iconClass: 'text-muted-foreground',
      chipClass: 'bg-muted',
      title: 'Nenhuma atividade no período',
      text: 'Não foram encontrados dados para o período selecionado.'
    });
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
      <h3 className="text-[15px] font-semibold text-foreground">Insights e Alertas</h3>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {insights.map((insight) => {
          const Icon = insight.icon;
          return (
            <div
              key={insight.title}
              className="flex items-start gap-3 rounded-xl border border-border/70 p-3.5 transition-colors duration-200 hover:border-border"
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${insight.chipClass}`}>
                <Icon className={`h-4 w-4 ${insight.iconClass}`} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{insight.title}</p>
                <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{insight.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
